function onAnimationEnd(elements, callback, options = { subtree: true }) {
  const animations = Array.isArray(elements)
    ? elements.flatMap((element) => element.getAnimations(options))
    : elements.getAnimations(options);
  const animationPromises = animations.reduce((acc, animation) => {
    // Ignore ViewTimeline animations
    if (animation.timeline instanceof DocumentTimeline) {
      acc.push(animation.finished);
    }

    return acc;
  }, /** @type {Promise<Animation>[]} */([]));

  return Promise.allSettled(animationPromises).then(callback);
}


/**
 * 判断元素是否至少有 75% 的高度处于当前视口内
 * @param {Element} element
 * @returns {boolean}
 */
function isElementMostlyInViewport(element) {
  const rect = element.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

  const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);

  if (visibleHeight <= 0) return false;

  return rect.height > 0 && visibleHeight / rect.height >= 0.6;
}


/* ============================================================================
   折扣码 —— 通用能力（单码 / 多码）
   任意 section / snippet / 外部脚本都可复用，且不依赖购物车折扣区块是否渲染。

   主路径使用 Storefront Events & Actions 的 updateCart
   （https://shopify.dev/docs/api/storefront-events-and-actions/actions/update-cart）：
   · 内部执行 cartDiscountCodesUpdate，券码挂在购物车对象上，随购物车进入 checkout
   · 折扣码大小写不敏感，且该 mutation 是「全量替换」，因此提交前会读回已有券码再合并
   · 失败以 userErrors 返回而不抛错；券码是否对当前购物车生效看 cart.discountCodes[].applicable
   · 主题已 updateCart.configure()，SDK 默认的整页 reload 不会触发

   兜底路径为 AJAX cart/update.js（{ discount: 'A,B' } 逗号分隔多码），
   与 sections/firmware-upgrade.liquid、snippets/product-promotion-code.liquid 的既有写法一致。

   两套提交语义，按调用方需要选：
   · applyDiscountCode() / applyDiscountCodes() —— 追加，保留车上已有券码（外部脚本、落地页）
   · setDiscountCodes()                        —— 覆盖，把集合同步成给定内容（折扣区块的 Apply，
                                                  因为输入框同时承担展示职责，必须能删）

   ⚠️ Liquid 的 cart 对象不暴露折扣码。官方文档里 cart 的 16 个属性没有 discount_codes，
   discount_application 也没有 code 属性（title 只是折扣后台名，不是券码）。
   所以 getToFetch() 拿回来的服务端 HTML 里折扣区块永远是空的，
   页面加载后必须由 JS 拉一次购物车，才能把已生效券码回填进输入框。
   ========================================================================== */

/** 「生效中」券码的本地存储键；值为逗号分隔的多码字符串，与主题既有实现保持一致 */
const DISCOUNT_STORAGE_KEY = 'CartDiscountcode';

/**
 * 券码集合变更事件。应用 / 移除 / 回滚都会广播 —— 对监听方来说关心的是
 * 「生效集合变了，该重读」，用一个事件即可覆盖，无须按动作分成多个。
 * detail: { codes: string[], applied: string[], failed: string[] }
 */
const DISCOUNT_CHANGED_EVENT = 'lute:discount-changed';

/** 店铺根路径，兼容 Shopify.routes 尚未就绪的场景 */
function getStoreRoot() {
  return window.Shopify?.routes?.root || window.T4SThemeSP?.root_url || '/';
}

/**
 * 读取本地缓存的「已生效」券码集合。
 * 兼容三种历史格式：'SAVE10'（单码）、'A,B'（逗号分隔，firmware-upgrade 等既有写法）、
 * '["A","B"]'（JSON 数组）
 */
function getSavedDiscountCodes() {
  let raw = '';
  try {
    raw = localStorage.getItem(DISCOUNT_STORAGE_KEY) || '';
  } catch (_) {
    return [];
  }

  const value = raw.trim();
  if (!value) return [];

  if (value.startsWith('[')) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return mergeCodes(parsed);
    } catch (_) { /* 非法 JSON 时落到分隔符解析 */ }
  }

  // mergeCodes 内部已按分隔符拆分，这里无需再 split
  return mergeCodes([value]);
}

/** 写入本地缓存；集合为空时直接清键，避免留下空串被当成「有一张空券」 */
function setSavedDiscountCodes(codes) {
  const merged = mergeCodes(codes);
  try {
    if (merged.length) localStorage.setItem(DISCOUNT_STORAGE_KEY, merged.join(','));
    else localStorage.removeItem(DISCOUNT_STORAGE_KEY);
  } catch (_) { /* localStorage 不可用（隐私模式等）时静默降级 */ }
}

/** 折扣码大小写不敏感（Shopify 定义），统一比较口径 */
function isSameCode(a, b) {
  return String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase();
}

/**
 * 券码分隔符：逗号（AJAX cart/update.js 官方文档里的多码分隔符）、分号、换行 / 制表符。
 * 刻意不把普通空格当分隔符 —— Shopify 券码不含空格，输入里的空格多半是笔误，
 * 拆开反而会把一个错码变成一串垃圾券码提交给购物车。
 * 每段仍会 trim，因此 "A, B, C" 这类带空格的粘贴串照常解析。
 */
const DISCOUNT_CODE_SEPARATOR = /[,，;；\r\n\t]+/;

/**
 * 归一化券码：拆分粘贴的多码串 → 去空格 → 按小写去重，保留首次出现的原始写法。
 * 因此 applyDiscountCode('A,B')、applyDiscountCodes(['A','B']) 与
 * 主题既有的 `codes.join(',')` 写法都归一到同一个集合。
 */
function mergeCodes(codes) {
  const seen = new Set();
  const merged = [];
  for (const raw of codes) {
    for (const part of String(raw ?? '').split(DISCOUNT_CODE_SEPARATOR)) {
      const code = part.trim();
      const key = code.toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      merged.push(code);
    }
  }
  return merged;
}

/**
 * 把购物车返回的券码归一化为 { code, applicable } 数组。
 * Storefront 返回对象数组，AJAX cart.js 只给字符串，两者在这里收敛。
 */
function normalizeCodeEntries(list) {
  const entries = [];
  const seen = new Set();

  for (const item of Array.isArray(list) ? list : []) {
    const raw = typeof item === 'string' ? item : item?.code;
    const code = String(raw ?? '').trim();
    const key = code.toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    // AJAX 侧不带 applicable，视为已被购物车接受
    const applicable = !(item && typeof item === 'object' && item.applicable === false);
    entries.push({ code, applicable });
  }

  return entries;
}

/** 取「生效中」的券码集合 —— 界面只展示真正起作用的券码 */
function applicableCodes(entries) {
  return mergeCodes(entries.filter((item) => item.applicable).map((item) => item.code));
}

/**
 * 券码集合变更的公共尾巴：推导「生效中」集合 → 回写本地缓存 → 广播事件。
 * 应用成功、部分失败回滚、移除，三条路径的收尾都在这里，避免事件名和缓存口径走样。
 *
 * @param {Array<{code: string, applicable: boolean}>} entries 变更后购物车上的券码条目
 * @param {{applied?: string[], failed?: string[]}} [detail] 本次提交的生效 / 未生效明细
 * @returns {string[]} 生效中的券码集合（同时已写入本地缓存）
 */
function publishDiscountChange(entries, detail = {}) {
  const codes = applicableCodes(entries);
  setSavedDiscountCodes(codes);
  document.dispatchEvent(new CustomEvent(DISCOUNT_CHANGED_EVENT, {
    detail: { codes, applied: detail.applied ?? [], failed: detail.failed ?? [] },
  }));
  return codes;
}

/** 两个券码集合是否等价（大小写不敏感；入参都经过 mergeCodes 去重，故可直接比长度） */
function isSameCodeSet(a, b) {
  return a.length === b.length && a.every((code) => b.some((other) => isSameCode(code, other)));
}

/**
 * 把若干券码映射回购物车返回的条目，用于查回它们的 applicable。
 * 后传入的覆盖先传入的 —— 需要「最近一次写入为准」时把旧结果放前面。
 */
function entriesOfCodes(codes, ...entryLists) {
  const byCode = new Map();
  for (const list of entryLists) {
    for (const item of list) byCode.set(item.code.toLowerCase(), item);
  }
  return codes.map((code) => byCode.get(code.toLowerCase())).filter(Boolean);
}

/**
 * 应用一个或多个折扣码（核心实现：纯逻辑，不碰任何 DOM，可独立复用）
 *
 * @param {string | string[]} input 折扣码，首尾空格会被忽略；可传数组批量应用
 * @param {{ replace?: boolean }} [options] replace=true 时把购物车券码集合同步成 input
 *   （可增可删，传空集合即清空）；默认 false = 只追加，不清掉车上已有的券码
 * @returns {Promise<{
 *   ok: boolean, code: string, codes: string[], applied: string[], failed: string[],
 *   applicable?: boolean, error?: string, message?: string
 * }>} 永不 reject，调用方无需 try/catch。三条字段在所有分支上都存在：
 *   codes   = 操作后购物车上「生效中」的完整券码集合
 *   applied = 本次提交里真正生效的券码（失败时非空即代表「部分成功」）
 *   failed  = 本次提交里未生效的券码
 */
async function applyCartDiscount(input, options = {}) {
  const replace = options.replace === true;
  const incoming = mergeCodes(Array.isArray(input) ? input : [input]);

  // 追加模式下空输入无事可做；同步模式下空输入是合法意图（= 清空全部券码）
  if (!incoming.length && !replace) {
    return { ok: false, code: '', codes: getSavedDiscountCodes(), applied: [], failed: [], error: 'EMPTY_CODE' };
  }

  const label = incoming.length === 1 ? incoming[0] : incoming.join(',');

  // updateCart 是全量替换语义：追加模式先读回车上已有券码再合并，避免误删其他券码
  const current = await readCartDiscountCodes();
  const before = current.codes.map((item) => item.code);
  const submitted = replace ? incoming : mergeCodes([...before, ...incoming]);
  const written = await writeCartDiscountCodes(submitted);

  if (!written.ok) {
    return {
      ok: false,
      code: label,
      codes: getSavedDiscountCodes(),
      applied: [],
      failed: incoming,
      error: written.error,
      message: written.message,
    };
  }

  // Shopify 官方口径：券码是否生效看返回的 cart.discountCodes[].applicable
  const byCode = new Map(written.codes.map((item) => [item.code.toLowerCase(), item]));
  const failed = incoming.filter((code) => byCode.get(code.toLowerCase())?.applicable !== true);

  // 本次提交里真正生效的券码 —— 调用方据此决定「要不要把界面收敛到实际状态」
  const applied = incoming.filter((code) => !failed.some((bad) => isSameCode(bad, code)));

  if (failed.length) {
    // 只清理未生效的券码，本次生效的照常保留 —— 提交 'A,B,aaa' 时 A、B 留在购物车上，
    // 只把 aaa 摘掉，不能因为一个错码把整批都撤销。
    //
    // 唯一例外：覆盖式提交「全军覆没」时整笔撤销回提交前。
    // 顾客把输入框从 'A' 改写成 'B' 而 B 又是错的，若照搬「只删错的」会得到空集合，
    // 连原有那张 A 也一起弄丢 —— 此时撤销这次编辑才是最少意外的结果。
    const allFailed = failed.length === incoming.length;
    const kept = replace && allFailed
      ? before
      : submitted.filter((code) => !failed.some((bad) => isSameCode(bad, code)));
    await writeCartDiscountCodes(kept);

    // applicable 以最近一次写入为准；撤销场景下 kept 里的券码只在上一次的读取结果里出现过，
    // 所以把变更前的读取结果放在前面，由 written 覆盖同一券码的旧标记
    const codes = publishDiscountChange(entriesOfCodes(kept, current.codes, written.codes), { applied, failed });
    return {
      ok: false,
      code: failed.length === 1 ? failed[0] : label,
      codes,
      applied,
      failed,
      error: byCode.has(failed[0].toLowerCase()) ? 'DISCOUNT_NOT_APPLICABLE' : 'DISCOUNT_CODE_INVALID',
    };
  }

  const codes = publishDiscountChange(written.codes, { applied: incoming });
  return { ok: true, code: label, applicable: true, codes, applied: incoming, failed: [] };
}

/**
 * 把购物车券码集合**同步**成给定内容（可增可删，传空集合即清空）。
 * 折扣区块的 Apply 走这里：输入框既是输入口也是展示口，
 * 顾客删掉其中一个券码再提交，购物车要跟着少一个 —— 纯追加做不到这件事。
 * @param {string | string[]} input 目标券码集合
 * @returns {ReturnType<typeof applyCartDiscount>}
 */
async function setCartDiscountCodes(input) {
  return applyCartDiscount(input, { replace: true });
}

/**
 * 移除一个折扣码。
 * @param {string} code 折扣码
 * @returns {Promise<{ok: boolean, code: string, codes: string[], error?: string, message?: string}>} 永不 reject
 */
async function removeCartDiscount(code) {
  const normalized = String(code ?? '').trim();
  if (!normalized) {
    return { ok: false, code: '', codes: getSavedDiscountCodes(), applied: [], failed: [], error: 'EMPTY_CODE' };
  }

  const current = await readCartDiscountCodes();
  const before = current.codes.map((item) => item.code);
  const next = before.filter((item) => !isSameCode(item, normalized));

  if (next.length === before.length) {
    // 车上本就没有这张券：不发请求，只把本地展示状态对齐到购物车
    const codes = applicableCodes(current.codes);
    setSavedDiscountCodes(codes);
    return { ok: true, code: normalized, codes, applied: [], failed: [] };
  }

  const written = await writeCartDiscountCodes(next);
  if (!written.ok) {
    return {
      ok: false,
      code: normalized,
      codes: getSavedDiscountCodes(),
      applied: [],
      failed: [normalized],
      error: written.error,
      message: written.message,
    };
  }

  return { ok: true, code: normalized, codes: publishDiscountChange(written.codes), applied: [], failed: [] };
}

/**
 * 兜底 configure：SDK 未被配置时会走默认分支（非 Dawn 系主题 = 整页 reload）。
 * configure() 只生效一次，重复调用返回 false，主题自身在 DOMContentLoaded 也配置同一份，
 * 这里只是堵住「DOMContentLoaded 之前就被外部脚本调用」的窗口。
 */
function ensureUpdateCartConfigured(actions) {
  const updateCart = actions?.updateCart;
  // actions 只有部分方法时（旧版 SDK / 测试替身）直接放过，不要在这里抛错
  if (!updateCart || typeof updateCart.configure !== 'function') return;
  if (typeof updateCart.isDefault === 'function' && !updateCart.isDefault()) return;
  updateCart.configure({
    eventTarget: () => document.querySelector('[data-cart-items]'),
  });
}

/**
 * 读取购物车当前的券码集合（含 applicable 标记）。
 * @returns {Promise<{ok: boolean, codes: Array<{code: string, applicable: boolean}>, error?: string}>}
 */
async function readCartDiscountCodes() {
  const actions = window.Shopify?.actions;

  if (typeof actions?.getCart === 'function') {
    ensureUpdateCartConfigured(actions);
    try {
      const { cart } = (await actions.getCart()) || {};
      return { ok: true, codes: normalizeCodeEntries(cart?.discountCodes) };
    } catch (_) {
      return { ok: false, codes: [], error: 'NETWORK_ERROR' };
    }
  }

  // 兜底：AJAX cart.js。discount_codes 未在官方文档列出，但线上确实返回（空车为 []）
  try {
    const response = await fetch(`${getStoreRoot()}cart.js`, { credentials: 'same-origin' });
    if (!response.ok) return { ok: false, codes: [], error: `HTTP_${response.status}` };
    const data = await response.json().catch(() => null);
    return { ok: true, codes: normalizeCodeEntries(data?.discount_codes) };
  } catch (_) {
    return { ok: false, codes: [], error: 'NETWORK_ERROR' };
  }
}

/**
 * 覆盖写入购物车券码集合（全量替换语义，传空数组即清空全部券码）。
 * @returns {Promise<{ok: boolean, codes: Array<{code: string, applicable: boolean}>, error?: string, message?: string}>}
 */
async function writeCartDiscountCodes(codes) {
  const list = mergeCodes(codes);
  const actions = window.Shopify?.actions;

  if (typeof actions?.updateCart === 'function') {
    ensureUpdateCartConfigured(actions);
    try {
      const result = await actions.updateCart({ discountCodes: list });
      const userError = result?.userErrors?.[0];
      if (userError) {
        return { ok: false, codes: [], error: userError.code || 'REJECTED', message: userError.message };
      }
      return { ok: true, codes: normalizeCodeEntries(result?.cart?.discountCodes) };
    } catch (_) {
      return { ok: false, codes: [], error: 'NETWORK_ERROR' };
    }
  }

  // 兜底：AJAX cart/update.js，discount 传逗号分隔多码，空串清空
  try {
    const response = await fetch(`${getStoreRoot()}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: list.join(',') }),
    });
    if (!response.ok) return { ok: false, codes: [], error: `HTTP_${response.status}` };

    const data = await response.json().catch(() => null);
    const echoed = normalizeCodeEntries(data?.discount_codes);
    // 兜底侧拿不到 applicable 时按「已提交」处理，无法判定券码是否真正生效
    return { ok: true, codes: echoed.length ? echoed : list.map((code) => ({ code, applicable: true })) };
  } catch (_) {
    return { ok: false, codes: [], error: 'NETWORK_ERROR' };
  }
}


/**
 * CartComponent — Mini Cart 内容自定义元素
 *
 * 职责划分：
 * - 抽屉开/关、遮罩、动画           → 由 T4S 框架 T4SThemeSP.Drawer 管理
 * - 购物车 section 初始加载/刷新    → 内部实现，取代 T4SThemeSP.Cart.init/getToFetch
 * - Sections 渲染                  → renderContents()，取代 T4SThemeSP.Cart.renderContents
 * - Note 保存                      → textarea[name="note"] change → Shopify.actions.updateCart({ note })
 * - 折扣码（支持多码）              → applyDiscountCode() / applyDiscountCodes() / setDiscountCodes()
 *                                   / removeDiscountCode()
 *                                   Storefront actions updateCart({ discountCodes }) + 输入框回填 + localStorage
 *                                   外部入口：window.T4SThemeSP.Cart.applyDiscountCode(code)
 * - 工具面板、Gift wrap、折扣码          → 新版已移除
 * - 购物车计数更新                   → cart:update:count 事件或 jQuery add:cart:success
 * - ATC 后行为                     → 由 T4Sconfigs.afterActionATC 决定（open drawer / redirect）
 * - Upsell 推荐                    → [data-cart-upsell-options] section fetch
 * - Confetti                       → 免运费达成时触发
 *
 * 不使用 Shadow DOM — 与项目现有规范一致
 *
 * 用法：
 *   <cart-component>
 *     ... mini_cart.liquid 的购物车内容 ...
 *   </cart-component>
 *
 * @extends {HTMLElement}
 */


class CartComponent extends HTMLElement {
  /** @type {AbortController | null} */
  #eventController = null;

  // ── 从 T4SThemeSP.Cart 迁移的状态 ──────────────────────
  /** 上次渲染的 upsell product_id，避免重复 fetch */
  #lastUpsellProductId = '';
  /** 免运费条是否已触发 confetti */
  #confettiShipDone = true;
  /** ATC 后是否需要刷新 upsell */
  #upsellPending = false;
  /**
   * 已绑定过事件的折扣码输入框，避免 section 重渲染后重复绑定
   * @type {HTMLInputElement | null}
   */
  #discountInput = null;

  /**
   * 用户是否正在手动编辑折扣码输入框。
   * 为 true 时渲染不覆盖输入框内容，避免把用户输入到一半的券码冲掉。
   */
  #discountDirty = false;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {number | undefined} */
  #resetTimeout;
  /** 刷新合并定时器：add:cart:success / cart:refresh 可能连续触发 */
  #refreshTimer;
  // ── Lifecycle ────────────────────────────────────────────

  constructor() {
    super();
  }

  connectedCallback() {
    this.#eventController = new AbortController();
    const signal = this.#eventController.signal;

    document.addEventListener('cart:refresh', this.#handleCartRefresh, { signal });
    document.addEventListener('cart:refresh:opend', this.#handleCartRefreshOpend, { signal });
    document.addEventListener('cart:update:count', this.#handleCountUpdate, { signal });
    document.addEventListener(DISCOUNT_CHANGED_EVENT, this.#handleDiscountChange, { signal });

    this.addEventListener('cart-item:qty-change', this.#handleQtyChange, { signal });
    this.addEventListener('click', this.#handleRemove, { signal });
    document.addEventListener('cart:variant-update', this.#handleVariantUpdate, { signal });

    this.#bridgeJQueryEvents();

    this.#resizeObserver = new ResizeObserver(() => this.#updateBottomHeight());
    this.#observeBottomBar();

    // upsell 在 ATC 成功后刷新
    const jq = window.jQuery || window.jQuery_T4NT;
    if (jq) {
      jq(document).on('add:cart:upsell', () => { this.#upsellPending = true; });
    }

    // 购物车页直出完整 HTML，无需 fetch
    if (!this.hasAttribute('data-cart-page')) {
      this.#loadInitialHtml();
    } else {
      this.#onHtmlReady();
    }
  }

  disconnectedCallback() {
    this.#eventController?.abort();
    this.#eventController = null;
    clearTimeout(this.#refreshTimer);
    this.#refreshTimer = undefined;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  /** 首次挂载时 fetch mini_cart section HTML 替换骨架屏，对齐原 T4SThemeSP.Cart.init 行为 */
  #loadInitialHtml() {
    if (window.IsDesignMode) {
      this.#onHtmlReady();
      return;
    }
    // theme.min.js 是懒加载的，用 $script 确保它就绪后再 fetch
    const load = () => {
      window.T4SThemeSP.getToFetchSection('?section_id=mini_cart').then((html) => {
        if (html === 'NVT_94') return;
        this.innerHTML = document.createRange().createContextualFragment(html).querySelector('*')?.innerHTML || html;
        this.#onHtmlReady();
      });
    };
    if (window.T4SThemeSP?.getToFetchSection) {
      load();
    } else {
      window.$script(window.T4Sconfigs.script2, load);
    }
  }

  /** HTML 就绪后的初始化（section 加载完成或设计器直出后调用） */
  #onHtmlReady() {
    this.#updateBottomHeight();
    this.#observeBottomBar();
    this.#initNote();
    this.#initDiscountCode();
    // this.#checkConfetti();
    this.#fetchUpsell();
    const jq = window.jQuery || window.jQuery_T4NT;
    window.T4SThemeSP?.Tooltip?.();
    jq?.(document).trigger('currency:update');
    document.dispatchEvent(new CustomEvent('cart:updated', {
      bubbles: true,
      cancelable: true,
      detail: { count: parseInt(this.dataset.ccount || '0', 10) },
    }));
  }

  // ── 公開 API（取代 T4SThemeSP.Cart）──────────────────────

  open() {
    T4SThemeSP.Drawer.opend($('#t4s-mini_cart'));
  }

  /**
   * 通用：应用折扣码并刷新购物车（公开 API，供任意 section / snippet / 外部脚本调用）
   *
   * 与折扣区块解耦：区块未渲染时依然可用；
   * 区块已渲染时，会通过 lute:discount-changed 事件自动回填输入框与「保存后」态。
   * 应用新券码时会保留购物车上已有的其他券码（底层是全量替换语义，提交前会先合并）。
   *
   * @param {string} code 折扣码，首尾空格会被忽略
   * @param {{ openCart?: boolean }} [options] openCart=true 时成功后打开购物车抽屉
   * @returns {Promise<{ok: boolean, code: string, codes: string[], applicable?: boolean, applied: string[], failed?: string[], error?: string, message?: string}>}
   *          永不 reject。error 取值：EMPTY_CODE / NETWORK_ERROR / DISCOUNT_CODE_INVALID /
   *          DISCOUNT_NOT_APPLICABLE / REJECTED（userErrors，附 message）/ HTTP_{status}（兜底方案）
   *          codes 为操作后购物车上「生效中」的完整券码集合
   *
   * @example
   *   const res = await window.T4SThemeSP.Cart.applyDiscountCode('SAVE10', { openCart: true });
   *   if (!res.ok) console.warn('折扣码未生效', res.error);
   */
  async applyDiscountCode(code, options = {}) {
    return this.#commitDiscount(applyCartDiscount(code), options);
  }

  /**
   * 通用：一次应用多个折扣码（落地页 ?discount=A,B 之类的场景）
   * @param {string[]} codes 折扣码数组
   * @param {{ openCart?: boolean }} [options] openCart=true 时成功后打开购物车抽屉
   * @returns {Promise<{ok: boolean, code: string, codes: string[], applied: string[], failed: string[], error?: string}>}
   */
  async applyDiscountCodes(codes, options = {}) {
    return this.#commitDiscount(applyCartDiscount(codes), options);
  }

  /**
   * 通用：把购物车券码集合**同步**成给定内容（可增可删，传空即清空全部）
   *
   * 与 applyDiscountCode() 的差别是「覆盖」而非「追加」。
   * 折扣区块的 Apply 按钮走的就是这里 —— 输入框同时承担展示职责，
   * 顾客删掉其中一个券码再提交时必须真的少一个。
   *
   * @param {string | string[]} codes 目标券码集合，支持 'A,B' 这样的粘贴串
   * @param {{ openCart?: boolean }} [options]
   * @returns {Promise<{ok: boolean, code: string, codes: string[], applied: string[], failed: string[], error?: string}>}
   *
   * @example
   *   await window.T4SThemeSP.Cart.setDiscountCodes(['SAVE10', 'FREESHIP']);
   *   await window.T4SThemeSP.Cart.setDiscountCodes(''); // 清空全部券码
   */
  async setDiscountCodes(codes, options = {}) {
    return this.#commitDiscount(setCartDiscountCodes(codes), options);
  }

  /**
   * 通用：移除一个已应用折扣码
   * @param {string} code 折扣码
   * @param {{ openCart?: boolean }} [options]
   * @returns {Promise<{ok: boolean, code: string, codes: string[], error?: string}>}
   *
   * @example
   *   await window.T4SThemeSP.Cart.removeDiscountCode('SAVE10');
   */
  async removeDiscountCode(code, options = {}) {
    return this.#commitDiscount(removeCartDiscount(code), options);
  }

  /**
   * 应用 / 移除券码后的统一收尾：刷新购物车金额，可选打开抽屉。
   * @param {Promise<{ok: boolean}>} pending 券码变更结果
   * @param {{ openCart?: boolean }} options
   */
  async #commitDiscount(pending, options) {
    const result = await pending;

    // 失败时购物车也可能被回滚过（脏码已移除），同样需要重渲染区块；
    // 输入框由 #discountDirty 保护，不会冲掉用户正在编辑的内容
    this.#renderDiscountBlock();
    if (!result.ok) return result;

    await this.getToFetch();
    if (options.openCart) this.open();

    return result;
  }

  /** 同步读取本地缓存的已生效券码（不发请求）；需要权威数据时用 hydrateDiscountCodes() */
  getDiscountCodes() {
    return getSavedDiscountCodes();
  }

  /**
   * 从购物车拉取券码集合并回写缓存 + 重渲染区块。
   * Liquid 拿不到折扣码，页面加载后必须靠这一步才能显示已应用券码。
   */
  async hydrateDiscountCodes() {
    const { ok, codes } = await readCartDiscountCodes();
    if (!ok) return [];

    const applicable = applicableCodes(codes);
    setSavedDiscountCodes(applicable);
    this.#renderDiscountBlock();
    return applicable;
  }

  /** 拉取最新购物车 sections 并渲染（取代 T4SThemeSP.Cart.getToFetch） */
  async getToFetch() {
    const sections = this.#getSectionParam();
    if (!window.T4SThemeSP?.getToFetchSection) return;
    const data = await window.T4SThemeSP.getToFetchSection('?sections=' + sections, 'json');
    if (data === 'NVT_94') return;
    this.renderContents(data);
  }

  /**
   * 渲染 sections 数据到 DOM（取代 T4SThemeSP.Cart.renderContents）
   * @param {Object} sections — { cart_data, mini_cart, [cartT4SectionID] }
   */
  renderContents(sections) {
    const cartData = sections.cart_data;
    const sectionId = this.dataset.sectionId || window.cartT4SectionID;
    const miniCartHtml = sections.mini_cart || sections[sectionId] || '';

    if (!cartData || !miniCartHtml) return;

    // 解析 cart_data 中的计数/价格/状态字段
    const rawData = cartData.split('[t4s_split1]')[1] || '';
    const parts = rawData.split('[t4s_split2]');
    const itemCount = parseFloat(parts[0]);

    // 更新 DOM：空购物车 / 有商品 结构不同，分两路处理
    const parsed = document.createRange().createContextualFragment(miniCartHtml);
    if (!this.querySelector('[data-cart-items]')) {
      // 当前是空购物车状态 → 整体替换 .t4s-mini_cart__form（覆盖空→空、空→有商品）
      this.#replaceInner('.t4s-mini_cart__form', parsed);
    } else if (itemCount === 0) {
      // 有商品 → 空：新 HTML 不含 [data-cart-items]，也需要整体替换
      this.#replaceInner('.t4s-mini_cart__form', parsed);
    } else {
      // 有商品 → 有商品：子区域替换
      this.#replaceInner('[data-cart-items]', parsed);
      this.#replaceInner('[data-cart-prices]', parsed);
      this.#replaceInner('[data-cart-total__bottom]', parsed);
    }

    this.#updateBottomHeight();
    this.#observeBottomBar();

    this.#replaceInner('[data-cart-header-count]', parsed);

    // 空↔非空切换会整体替换表单，折扣码输入框需要重新绑定（同元素时内部直接跳过）
    this.#initDiscountCode();

    // 触发下游更新
    document.dispatchEvent(new CustomEvent('cart:update:count', {
      bubbles: true, cancelable: true, detail: { count: itemCount },
    }));
    window.T4SThemeSP?.Tooltip?.();
    const jq = window.jQuery || window.jQuery_T4NT;
    jq?.(document).trigger('currency:update');
    document.dispatchEvent(new CustomEvent('cart:updated', {
      bubbles: true, cancelable: true, detail: { count: itemCount },
    }));
    jq?.(document).trigger('cart:updated');

    // 购物车页不执行 ATC 后行为（无需开抽屉或跳转）
    if (!this.hasAttribute('data-cart-page')) {
      const afterAction = window.T4Sconfigs?.afterActionATC;
      if (afterAction && afterAction !== '0' && afterAction !== '1' && afterAction !== '2' && window.T4SThemeSP?.isATCSuccess) {
        this.#handleAfterATC();
      }
    }

    this.#checkConfetti();

    if (this.#upsellPending) {
      this.#upsellPending = false;
      this.#fetchUpsell();
    }

  }

  // ── 内部初始化 ────────────────────────────────────────────

  #getSectionParam() {
    const isCartPage = window.T4Sconfigs?.cartType !== 'disable'
      && window.location.pathname.includes('/cart');
    if (isCartPage) {
      const sectionId = this.dataset.sectionId || window.cartT4SectionID;
      return `cart_data,${sectionId}`;
    }
    const isDisable = window.T4Sconfigs?.cartType === 'disable';
    return isDisable ? 'cart_data' : 'cart_data,mini_cart';
  }

  #initNote() {
    const textarea = this.querySelector('textarea[name="note"]');
    if (!textarea) return;
    textarea.addEventListener('change', () => {
      Shopify.actions.updateCart({ note: textarea.value })
        .then(({ userErrors }) => {
          if (userErrors?.length) throw new Error(userErrors[0].message);
        })
        .catch((err) => console.warn('[cart-component] note update failed:', err));
    });
  }

  /**
   * 折扣码区块：输入框 + Apply。
   * · 输入框既是输入口也是展示口 —— 已生效券码以逗号连接回填（多码就是 `A,B`），
   *   顾客改写这段文字再提交即可增删，沿用 Figma「未激活 / 激活 / 保存后 / 聚焦再次激活」四态
   * · 提交走 setDiscountCodes()（覆盖式），与外部脚本用的 applyDiscountCode()（追加式）
   *   共用同一条 Storefront actions 链路，失败态在此渲染，不存在第二套请求逻辑
   */
  #initDiscountCode() {
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('#CartDiscountcode'));
    if (!input || input === this.#discountInput) return;
    this.#discountInput = input;

    const block = input.closest('[data-cart-coupon]');
    const btn = this.querySelector('[data-cart-coupon-save]');
    const tips = this.querySelector('[data-cart-coupon-tips]');
    const tipsText = tips ? tips.textContent.trim() : '';
    const errorText = tips?.dataset.errorText || '';

    /** 编辑中：不回填输入框，只按实时内容刷新按钮态 */
    const markDirty = () => {
      this.#discountDirty = true;
      this.#renderDiscountBlock();
    };

    // 先用本地缓存渲染（避免闪烁），再拉购物车校正
    this.#discountDirty = false;
    this.#renderDiscountBlock();
    this.hydrateDiscountCodes();

    input.addEventListener('input', markDirty);

    /**
     * 再次激活：聚焦即退出置灰态并放开按钮，允许原样重提交。
     * 这里不走 #renderDiscountBlock()——内容没变时它会判定「无变化」而立刻重新置灰。
     */
    input.addEventListener('focus', () => {
      if (!block?.classList.contains('is--saved')) return;
      this.#discountDirty = true;
      block.classList.remove('is--saved');
      if (btn) btn.disabled = false;
    });

    /**
     * 提交：把购物车券码集合同步成输入框里的内容。
     * 输入框就是展示口，所以「删掉其中一个再提交」也必须生效 —— 用同步而非追加。
     */
    const applyCode = async () => {
      if (block?.classList.contains('is--loading')) return;
      if (btn?.disabled) return;

      block?.classList.remove('is--error');
      if (tips && tipsText) tips.textContent = tipsText;
      block?.classList.add('is--loading');

      try {
        const result = await this.setDiscountCodes(input.value);
        if (!result.ok) {
          block?.classList.add('is--error');
          if (tips && errorText) tips.textContent = errorText;
          console.warn('[cart-component] discount apply failed:', result.error, result.failed);
        }
        // 交回状态驱动：输入框按实际生效的券码回填。
        // 部分成功时（如 'A,B,aaa'）连带把写错的那个从输入框里清掉；
        // 全军覆没时保留顾客原文，好让他直接改错字而不用重新输一遍。
        if (result.ok || result.applied?.length) this.#discountDirty = false;
      } finally {
        block?.classList.remove('is--loading');
        this.#renderDiscountBlock();
      }
    };

    btn?.addEventListener('click', applyCode);
    input.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      applyCode();
    });
  }

  /** ATC 后行为：open drawer(3) 或 redirect(4=cart page, 5=checkout) */
  #handleAfterATC() {
    const R = window.T4Sconfigs?.afterActionATC;
    if (!R || R === '0' || R === '1' || R === '2') return;
    window.T4SThemeSP.isATCSuccess = false;
    if (R === '3') {
      this.open();
    } else {
      const cartUrl = window.T4Sroutes?.cart_url || '/cart';
      window.location.href = R === '5' ? (window.T4SThemeSP?.root_url || '') + 'checkout' : cartUrl;
    }
  }

  async handleFlyToCart(target, imgOrEl) {
    const cartIcon = document.querySelector('.btn-groups__btn.tdf_cart_icon');

    if (!cartIcon || !target || !imgOrEl) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const isElement = imgOrEl instanceof Element;
    let imgSrc = isElement
      ? (imgOrEl.dataset.src || imgOrEl.dataset.orginal || imgOrEl.currentSrc || imgOrEl.src)
      : imgOrEl;
    // source 必须是 DOM 元素供 IntersectionObserver 取位置；字符串 URL 时退用 target

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${imgSrc})`);
    flyToCartElement.source = target;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([target, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      target.removeAttribute('data-added');
    }, 800);
  };

  /** Confetti：免运费区间变化时触发 */
  #checkConfetti() {
    const shipDoneEls = this.querySelectorAll('[data-cart-ship-done]');
    if (!window.T4SThemeSP?.CanvasConfetti) return;
    if (shipDoneEls.length > 0 && this.#confettiShipDone) {
      window.T4SThemeSP.CanvasConfetti();
      this.#confettiShipDone = false;
    } else if (shipDoneEls.length === 0) {
      this.#confettiShipDone = true;
    }
  }

  /** 通知 <cart-recommend> 更新推荐商品 */
  #fetchUpsell() {
    const el = this.querySelector('cart-recommend');
    if (!el) return;
    const pid = this.querySelector('[data-cart-items] [data-cart-item]:first-child')?.dataset.pid
      || el.getAttribute('product-id')
      || '';
    if (!pid || pid === this.#lastUpsellProductId) return;
    this.#lastUpsellProductId = pid;
    el.setAttribute('product-id', pid);
  }

  /** 替换组件内匹配选择器的元素内容 */
  #replaceInner(selector, fragment) {
    const target = this.querySelector(selector);
    const source = fragment.querySelector(selector);
    if (target && source) target.innerHTML = source.innerHTML;
  }

  // ── Bottom bar dynamic height ─────────────────────────────

  #updateBottomHeight() {
    const bar = this.querySelector('[data-cart-total__bottom]');
    const h = bar ? bar.offsetHeight : 0;
    document.body.style.setProperty('--cart-bottom-h', h + 'px');
  }

  #observeBottomBar() {
    this.#resizeObserver?.disconnect();
    const bar = this.querySelector('[data-cart-total__bottom]');
    if (bar) this.#resizeObserver?.observe(bar);
  }

  // ── Event handlers ────────────────────────────────────────
  /** cart:refresh → 静默拉取并渲染 */
  #handleCartRefresh = () => {
    this.#scheduleRefresh();
  };

  /**
   * 合并刷新请求。
   *
   * 一次加购可能同时触发 jQuery `add:cart:success` 与原生 `cart:refresh`
   * （例如产品卡按钮两条都发），短时间内的重复触发只保留最后一次，
   * 避免同一份购物车被并发拉取多次、后到的旧响应覆盖新内容。
   */
  #scheduleRefresh(delay = 60) {
    clearTimeout(this.#refreshTimer);
    this.#refreshTimer = setTimeout(() => {
      this.#refreshTimer = undefined;
      this.getToFetch();
    }, delay);
  }

  /** cart:refresh:opend → 拉取并打开抽屉 */
  #handleCartRefreshOpend = () => {
    this.getToFetch(true);
  };

  /**
   * 券码集合变化后重渲染折扣区块。
   * 触发方式：DISCOUNT_CHANGED_EVENT（applyCartDiscount / removeCartDiscount 广播）
   * 本地缓存已在广播前写好，因此这里只需重渲染，不必再次请求购物车。
   *
   * 注意这里**不**清 #discountDirty：区块自身的 Apply 也会走到这条广播，
   * 清掉脏标记等于让事件把自己刚保护起来的顾客输入冲掉。
   * 是否允许回填交给 #renderDiscountBlock() 按脏标记判断。
   */
  #handleDiscountChange = () => {
    if (!this.querySelector('#CartDiscountcode')) return;
    this.#renderDiscountBlock();
  };

  /**
   * 同步渲染折扣区块（幂等，可反复调用）。
   *
   * 输入框既是输入口也是展示口：已生效券码以逗号连接回填，多码就是 `A,B`。
   * 顾客直接改写这段文字再点 Apply 即可增删 —— 这与 Figma 单码稿的
   * 「保存后置灰 / 聚焦再次激活」是同一套交互，只是内容从一张券变成一组券。
   *
   * 用户正在编辑时（#discountDirty）不回填，避免冲掉输入；此时按钮态仍按实时内容计算。
   */
  #renderDiscountBlock() {
    const block = this.querySelector('[data-cart-coupon]');
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('#CartDiscountcode'));
    if (!input) return;

    const btn = this.querySelector('[data-cart-coupon-save]');
    const codes = getSavedDiscountCodes();

    if (!this.#discountDirty) input.value = codes.join(',');

    // 输入内容与车上集合一致 = 没有可提交的变化，进入「保存后」置灰态
    const typedCodes = mergeCodes([input.value]);
    const changed = !isSameCodeSet(typedCodes, codes);
    const isSaved = !changed && typedCodes.length > 0;

    block?.classList.toggle('is--saved', isSaved);
    if (btn) btn.disabled = !changed;

    // 保留 name 以兼容原生表单提交 / 无 JS 回退路径
    if (input.value.trim()) input.setAttribute('name', 'discount');
    else input.removeAttribute('name');
  }

  #handleCountUpdate = (event) => {
    const count = event.detail?.count ?? 0;
    this.#updateCartCount(count);
  };

  #updateCartCount(count) {
    let countEls = document.querySelectorAll('[data-cart-count]');
    // badge 仅在服务端 count>0 时渲染，首次从 0 变为非 0 时需动态创建
    if (count > 0 && countEls.length === 0) {
      document.querySelectorAll('.tdf_cart_icon').forEach((icon) => {
        const span = document.createElement('span');
        span.setAttribute('data-cart-count', '');
        span.className = 'count-box';
        icon.appendChild(span);
      });
      countEls = document.querySelectorAll('[data-cart-count]');
    }
    countEls.forEach((el) => {
      el.hidden = count === 0;
      el.textContent = String(count);
    });
    this.dataset.ccount = String(count);
    document.body.classList.toggle('t4s-cart-count-0', count === 0);
  }

  #setItemLoading(cartItem) {
    this.classList.add('is--contentUpdate');
    cartItem?.classList.add('is--update');
  }

  #clearItemLoading(cartItem) {
    this.classList.remove('is--contentUpdate');
    cartItem?.classList.remove('is--update');
  }

  #handleRemove = (event) => {
    const btn = event.target.closest('.cart-item__remove');
    if (!btn) return;
    event.preventDefault();
    const key = btn.dataset.id;
    if (!key) return;
    const cartItem = btn.closest('[data-cart-item]');
    this.#setItemLoading(cartItem);
    Shopify.actions.updateCart({ lines: [{ id: key, quantity: 0 }] })
      .then(({ userErrors }) => {
        if (userErrors?.length) throw new Error(userErrors[0].message);
        return this.getToFetch(false);
      })
      .catch((err) => console.error('[cart-component] remove failed:', err))
      .finally(() => { this.#clearItemLoading(cartItem); });
  };

  #handleQtyChange = (event) => {
    const { key, qty } = event.detail;
    if (!key) return;
    const cartItem = event.target.closest('[data-cart-item]');
    this.#setItemLoading(cartItem);
    Shopify.actions.updateCart({ lines: [{ id: key, quantity: qty }] })
      .then(({ userErrors }) => {
        if (userErrors?.length) throw new Error(userErrors[0].message);
        return this.getToFetch(false);
      })
      .catch((err) => console.error('[cart-component] qty change failed:', err))
      .finally(() => { this.#clearItemLoading(cartItem); });
  };

  #handleVariantUpdate = (e) => {
    const { cartKey, variantId, qty } = e.detail;
    if (!cartKey || !variantId) return;
    const cartItem = document.querySelector(`[data-id="${CSS.escape(cartKey)}"]`)?.closest('[data-cart-item]');
    this.#setItemLoading(cartItem);
    Shopify.actions.updateCart({
      lines: [
        { id: cartKey, quantity: 0 },
        { merchandiseId: String(variantId), quantity: qty },
      ],
    })
      .then(({ userErrors }) => {
        if (userErrors?.length) throw new Error(userErrors[0].message);
        return this.getToFetch(false);
      })
      .catch((err) => console.error('[cart-component] variant update failed:', err))
      .finally(() => { this.#clearItemLoading(cartItem); });
  };


  // ── jQuery bridge ─────────────────────────────────────────

  #bridgeJQueryEvents() {
    const jq = window.jQuery || window.jQuery_T4NT;
    if (!jq || typeof jq !== 'function') return;

    // 加购成功 → 更新计数并刷新购物车内容
    // 主题各处（interactable.min.js、pdp.js、各产品卡按钮）加购成功后只 trigger 这个
    // jQuery 事件，不再走 T4SThemeSP.Cart 的 renderContents，所以必须在这里补刷新，
    // 否则抽屉/购物车页内容停留在加购前的状态。
    jq(document).on('add:cart:success', (event, responseJson) => {
      if (responseJson?.item_count != null) {
        this.#updateCartCount(responseJson.item_count);
      }
      this.#scheduleRefresh();
    });
  }

  static debounce(fn, delay) {
    let timer = null;
    return function (...args) {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }
}

if (!customElements.get('cart-component')) {
  customElements.define('cart-component', CartComponent);
}

// ── CartItemStepper custom element ───────────────────────────────────────────
class CartItemStepper extends HTMLElement {
  static get observedAttributes() {
    return ['data-value', 'data-min', 'data-max'];
  }

  connectedCallback() {
    if (this._init) return;
    this._init = true;

    this._decBtn = this.querySelector('[data-decrease-qty]');
    this._incBtn = this.querySelector('[data-increase-qty]');
    this._input = this.querySelector('input');
    if (!this._input) return;

    this.addEventListener('click', this);
    this._input.addEventListener('change', this);
    this._updateState();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this);
    this._input?.removeEventListener('change', this);
  }

  handleEvent(e) {
    if (e.type === 'click') {
      const btn = e.target.closest('[data-decrease-qty], [data-increase-qty]');
      if (!btn) return;
      e.stopPropagation();
      const cur = parseInt(this._input.value, 10) || this._min;
      const next = btn.hasAttribute('data-decrease-qty')
        ? Math.max(this._min, cur - this._step)
        : Math.min(this._max, cur + this._step);
      if (next !== cur) {
        this._input.value = String(next);
        this._input.dataset.currentQty = String(next);
        this.dataset.value = String(next);
        this._updateState();
        this._dispatch(next);
      }
    } else if (e.type === 'change') {
      const raw = parseInt(this._input.value, 10);
      const aligned = isNaN(raw) ? this._min : Math.round(raw / this._step) * this._step;
      const clamped = Math.max(this._min, Math.min(this._max, aligned));
      const val = clamped !== raw ? clamped : raw;
      if (clamped !== raw) {
        this._input.value = String(clamped);
        this._input.dataset.currentQty = String(clamped);
        this.dataset.value = String(clamped);
      }
      this._updateState();
      this._dispatch(val);
    }
  }

  attributeChangedCallback(name) {
    if (!this._init || !this._input) return;
    if (name === 'data-value') {
      const val = this._clamp(parseInt(this.dataset.value, 10) || this._min);
      this._input.value = String(val);
      this._input.dataset.currentQty = String(val);
    }
    if (name === 'data-min' || name === 'data-max') {
      this._input.min = String(this._min);
      this._input.max = String(this._max);
      const clamped = this._clamp(parseInt(this._input.value, 10));
      this._input.value = String(clamped);
    }
    this._updateState();
  }

  get _min() { return parseInt(this.dataset.min, 10) || 1; }
  get _max() { return parseInt(this.dataset.max, 10) || 9999; }
  get _step() { return parseInt(this.dataset.step, 10) || 1; }

  _clamp(val) { return Math.max(this._min, Math.min(this._max, isNaN(val) ? this._min : val)); }

  _dispatch(qty) {
    const key = this._input.dataset.id;
    this.dispatchEvent(new CustomEvent('cart-item:qty-change', {
      bubbles: true,
      detail: { key, qty },
    }));
  }

  _updateState() {
    const val = parseInt(this._input.value, 10);
    this._decBtn.disabled = val <= this._min;
    this._incBtn.disabled = val >= this._max;
  }
}

if (!customElements.get('cart-item-stepper')) {
  customElements.define('cart-item-stepper', CartItemStepper);
}


// 暴露到全局命名空间
window.T4SThemeSP = window.T4SThemeSP || {};
window.T4SThemeSP.CartComponent = CartComponent;

// ── CartRecommend custom element ─────────────────────────────────────────────
/**
 * <cart-recommend> — mini cart 推荐区域
 *
 * 属性：
 *   product-id   当前购物车第一件商品 ID（由 CartComponent 写入，变化时自动重新 fetch）
 *   section-id   Shopify section 名称，默认 mini_cart_upsell_cards
 *   limit        最多显示条数，默认 5
 *
 * fetch URL 格式：/recommendations/products?section_id=<id>&product_id=<pid>&limit=<n>
 */
class CartRecommend extends HTMLElement {
  static get observedAttributes() { return ['product-id']; }

  #abortController = null;

  connectedCallback() {
    const pid = this.getAttribute('product-id');
    this.#fetch(pid);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (name === 'product-id' && newVal && newVal !== oldVal) {
      this.#fetch(newVal);
    }
  }

  #fetch(pid) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const baseUrl = this.getAttribute('base-url')
    const sectionId = this.getAttribute('section-id') || 'mini_cart_upsell_cards';
    const limit = this.getAttribute('limit');
    const url = `${baseUrl}?section_id=${encodeURIComponent(sectionId)}&limit=${encodeURIComponent(limit)}&product_id=${encodeURIComponent(pid)}`;

    fetch(url, { signal: this.#abortController.signal })
      .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
      .then((html) => {
        const frag = document.createRange().createContextualFragment(html);
        const inner = frag.querySelector('div')?.innerHTML ?? html;
        this.innerHTML = inner;
        const empty = this.innerHTML.trim() === '';
        this.hidden = empty;
        // 同步隐藏/显示整个推荐区块（含标题）
        const section = this.closest('.cart-upsell__section');
        if (section) section.hidden = empty;
        // 触发货币刷新（T4S 多币种）
        (window.jQuery || window.jQuery_T4NT)?.(document).trigger('currency:update');
      })
      .catch((err) => {
        if (err?.name !== 'AbortError') console.error('[cart-recommend] fetch failed:', err);
      });
  }
}

if (!customElements.get('cart-recommend')) {
  customElements.define('cart-recommend', CartRecommend);
}

/**
 * fly-to-cart.js — 加购飞入购物车动画 (Web Component)
 *
 * 用法：在 ATC 点击时创建元素并设置 source / destination 属性：
 *   const el = document.createElement('fly-to-cart');
 *   el.source      = productImg;
 *   el.destination = cartIcon;
 *   document.body.appendChild(el);
 *
 * 元素 connectedCallback 后自动执行动画，结束时自行 remove()。
 * 动画由 CSS @keyframes 驱动，JS 只负责设置 CSS 自定义属性。
 */

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    const observer = new IntersectionObserver((entries) => {
      /** @type {DOMRectReadOnly|null} */
      let sourceRect = null;
      /** @type {DOMRectReadOnly|null} */
      let destinationRect = null;

      for (const entry of entries) {
        if (entry.target === this.source) sourceRect = entry.boundingClientRect;
        if (entry.target === this.destination) destinationRect = entry.boundingClientRect;
      }

      if (sourceRect && destinationRect) {
        observer.disconnect();
        this.#animate(sourceRect, destinationRect);
      }
    });

    observer.observe(this.source);
    observer.observe(this.destination);
  }

  /**
   * @param {DOMRectReadOnly} srcRect
   * @param {DOMRectReadOnly} destRect
   */
  async #animate(srcRect, destRect) {
    const startX = srcRect.left + srcRect.width / 2;
    const startY = srcRect.top + srcRect.height / 2;
    const endX = destRect.left + destRect.width / 2;
    const endY = destRect.top + destRect.height / 2;

    this.style.setProperty('--ftc-start-x', `${startX}px`);
    this.style.setProperty('--ftc-start-y', `${startY}px`);
    this.style.setProperty('--ftc-travel-x', `${endX - startX}px`);
    this.style.setProperty('--ftc-travel-y', `${endY - startY}px`);
    this.style.setProperty('--ftc-size', `${Math.min(srcRect.width, srcRect.height, 80)}px`);

    // yield so the browser applies the custom properties before animations start
    await new Promise((r) => requestAnimationFrame(r));

    await Promise.allSettled(this.getAnimations().map((a) => a.finished));

    this.destination.classList.add('fly-to-cart--bounce');
    this.destination.addEventListener('animationend', () => {
      this.destination.classList.remove('fly-to-cart--bounce');
    }, { once: true });

    this.remove();
  }
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}



// 替代 T4SThemeSP.Cart 的公共接口，供 b2g.js / cart-b2g.js / pdp.js 调用
window.T4SThemeSP.Cart = {
  _cartCache: null,
  _cart() {
    if (!this._cartCache) {
      this._cartCache = document.querySelector('cart-component');
    }
    return this._cartCache;
  },
  /**
   * 通用：应用折扣码（外部脚本 / Liquid 推荐入口）
   *
   * 券码挂到购物车对象上（Storefront actions updateCart，兜底 AJAX cart/update.js），
   * 成功后自动刷新购物车金额。应用新券码不会顶掉车上已有的其他券码。
   * 折扣区块若已渲染，会回填输入框与「保存后」态；未渲染也不影响折扣生效。
   *
   * @param {string} code 折扣码
   * @param {{ openCart?: boolean }} [options] openCart=true 时成功后打开购物车抽屉
   * @returns {Promise<{ok: boolean, code: string, codes: string[], applied: string[], failed: string[], error?: string}>} 永不 reject
   *
   * @example
   *   // 任意 section / snippet / 控制台
   *   const res = await window.T4SThemeSP.Cart.applyDiscountCode('SAVE10', { openCart: true });
   *   if (!res.ok) console.warn('折扣码无效：', res.error); // EMPTY_CODE / DISCOUNT_NOT_APPLICABLE / ...
   */
  applyDiscountCode(code, options) {
    const cart = this._cart();
    // 无 cart-component（极简模板）时退化为纯逻辑：仍然写购物车 + localStorage
    return cart ? cart.applyDiscountCode(code, options) : applyCartDiscount(code);
  },
  /**
   * 通用：一次应用多个折扣码（落地页 ?discount=A,B 之类的场景）
   * @param {string[]} codes 折扣码数组
   * @param {{ openCart?: boolean }} [options]
   * @returns {Promise<{ok: boolean, code: string, codes: string[], applied: string[], failed: string[], error?: string}>}
   *
   * @example
   *   await window.T4SThemeSP.Cart.applyDiscountCodes(['SAVE10', 'FREESHIP']);
   */
  applyDiscountCodes(codes, options) {
    const cart = this._cart();
    return cart ? cart.applyDiscountCodes(codes, options) : applyCartDiscount(codes);
  },
  /**
   * 通用：把券码集合同步成给定内容（覆盖式，传空即清空）
   * @param {string | string[]} codes
   * @param {{ openCart?: boolean }} [options]
   * @returns {Promise<{ok: boolean, code: string, codes: string[], applied: string[], failed: string[], error?: string}>}
   *
   * @example
   *   await window.T4SThemeSP.Cart.setDiscountCodes('SAVE10,FREESHIP');
   *   await window.T4SThemeSP.Cart.setDiscountCodes(''); // 清空
   */
  setDiscountCodes(codes, options) {
    const cart = this._cart();
    return cart ? cart.setDiscountCodes(codes, options) : setCartDiscountCodes(codes);
  },
  /**
   * 通用：移除一个已应用折扣码
   * @param {string} code 折扣码
   * @param {{ openCart?: boolean }} [options]
   * @returns {Promise<{ok: boolean, code: string, codes: string[], error?: string}>}
   *
   * @example
   *   await window.T4SThemeSP.Cart.removeDiscountCode('SAVE10');
   */
  removeDiscountCode(code, options) {
    const cart = this._cart();
    return cart ? cart.removeDiscountCode(code, options) : removeCartDiscount(code);
  },
  /** 同步读取本地缓存的已生效券码（不发请求） */
  getDiscountCodes() {
    return getSavedDiscountCodes();
  },
  /** 拉取购物车券码集合并同步到折扣区块（Liquid 拿不到券码，页面加载后需要这一步） */
  hydrateDiscountCodes() {
    return this._cart()?.hydrateDiscountCodes() ?? Promise.resolve(getSavedDiscountCodes());
  },
  getToFetch() {
    this._cart()?.getToFetch();
  },
  renderContents(sections) {
    this._cart()?.renderContents(sections);
  },
  /** sections 有值时直接渲染，否则重新拉取 */
  refresh(sections) {
    sections ? this.renderContents(sections) : this.getToFetch();
  },
  isElementMostlyInViewport: isElementMostlyInViewport,
  flyToCart(target, ImgOrSrc) {
    this._cart()?.handleFlyToCart(target, ImgOrSrc);
  },
  open() {
    this._cart()?.open();
  },
  /** init 已由 connectedCallback 驱动，此处为兼容旧调用 */
  init() { },
};


// 阻止 Shopify.actions.updateCart 默认的全页 reload 行为。
// 只需提供 eventTarget，默认 SFAPI handler 仍然运行，
// 事件从返回的元素 dispatch，购物车页 interactable.js 自行处理 DOM 刷新。
document.addEventListener('DOMContentLoaded', () => {
  Shopify.actions.openCart.configure({
    handler() {
      window.T4SThemeSP.Cart?.open();
    },
  });
  // 仅在购物车页阻止默认全页 reload
  Shopify.actions.updateCart.configure({
    eventTarget: () => document.querySelector('[data-cart-items]'),
  });
});


window.luteTheme = window.luteTheme || {};

window.luteTheme.cartEvents = {
  _cart() {
    return document.querySelector('cart-component');
  },

  initQuickshop() {
    const quickshopOverlay = document.getElementById('quickshop-modal-overlay');
    if (quickshopOverlay) {
      quickshopOverlay._dsmOnConfirm = async () => {
        const pickerModal = quickshopOverlay.querySelector('product-picker-modal');
        const data = pickerModal?.collect();
        if (!data) return;
        await Shopify.actions.updateCart({
          lines: [{ merchandiseId: String(data.variantId), quantity: data.qty }],
        })
          .then(() => {
            window.DsmDialog?.close('quickshop-modal');
            window.luteTheme.cartEvents._cart()?.getToFetch(false);
          })
          .catch((err) => console.error('[CartComponent] quickshop add failed:', err));
      };
    }
  },


  init() {
    this.initQuickshop();
  },
};

window.luteTheme.cartEvents.init();

// window.T4SThemeSP.Cart?.open();


(function () {
  window.luteTheme = window.luteTheme || {}

  window.luteTheme.loadFeature = function (options) {
    var configElement = options.configElement || (options.configId ? document.getElementById(options.configId) : null)
    var promiseKey = options.promiseKey
    var requireContent = options.requireContent !== false

    if (configElement) {
      if (requireContent && !configElement.textContent.trim()) return promiseKey ? window[promiseKey] : null

      options.src = options.src || configElement.dataset.src
    }

    if (!options.src) return promiseKey ? window[promiseKey] : null

    if (!promiseKey) promiseKey = '__luteFeaturePromise:' + options.src

    window[promiseKey] ||= import(options.src).catch(function (error) {
      window[promiseKey] = null
      throw error
    })

    return window[promiseKey]
  }

  window.luteTheme.features = window.luteTheme.features || [
    { configId: 't4s-gwp-config', promiseKey: '__cartGwpPromise' },
    { configId: 't4s-b2g-config', promiseKey: '__cartB2gPromise' },
  ]

  window.luteTheme.loadConfiguredFeatures = function (features) {
    var promises = (features || window.luteTheme.features || []).map(function (feature) {
      return window.luteTheme.loadFeature(feature)
    })

    Promise.all(promises.map(function (promise) {
      return Promise.resolve(promise).catch(function () { return null })
    })).then(function () {
      var root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/'
      var cartPromise = fetch(root + 'cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null })
        .catch(function () { return null })
      document.dispatchEvent(new CustomEvent('lute:cart-mutation', {
        bubbles: true,
        detail: { isAdd: false, cart: cartPromise },
      }))
    })

    return promises
  }

  window.luteTheme.init = function () {
    window.luteTheme.loadConfiguredFeatures()
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', window.luteTheme.init, { once: true })
  } else {
    window.luteTheme.init()
  }
})()

